/**
 * What: Renders saved Shopify micro-surveys and their lifecycle actions.
 * Transport / data-flow: Receives TestFeed-owned survey records loaded through the Shopify Worker admin route.
 *
 */
import { Badge, BlockStack, Button, Card, InlineError, InlineStack, Text } from '@shopify/polaris'
import { supportedMicroSurveyTriggers } from './microSurveyDraft'
import type { SavedMicroSurvey } from './types'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { ShopifyPageSkeleton } from '../ShopifyPageSkeleton'

type MicroSurveyListProps = {
  microSurveys: SavedMicroSurvey[]
  isLoading: boolean
  error: string | null
  archivingSurveyId: string | null
  onRetry: () => void
  onArchive: (survey: SavedMicroSurvey) => void
}

function triggerLabel(survey: SavedMicroSurvey) {
  const triggerType = survey.payload.triggerCondition?.type ?? survey.payload.placements?.[0]?.type
  const trigger = supportedMicroSurveyTriggers.find((candidate) => candidate.type === triggerType)
  return trigger?.label ?? 'Unsupported trigger'
}

function statusBadge(status: SavedMicroSurvey['status']) {
  if (status === 'active')
    return { tone: 'success' as const, label: shopifyComponentCopy.microSurveysMicroSurveyList.active001 }
  return { tone: 'attention' as const, label: shopifyComponentCopy.microSurveysMicroSurveyList.archived002 }
}

function surveyQuestion(survey: SavedMicroSurvey) {
  return survey.payload.questions[0]?.text ?? survey.surveyId
}

function MicroSurveyRow({
  survey,
  archivingSurveyId,
  onArchive,
}: {
  survey: SavedMicroSurvey
  archivingSurveyId: string | null
  onArchive: (survey: SavedMicroSurvey) => void
}) {
  const badge = statusBadge(survey.status)
  const canArchive = survey.status === 'active'
  const archiveAction = canArchive ? (
    <Button
      onClick={() => onArchive(survey)}
      loading={archivingSurveyId === survey.surveyId}
      disabled={Boolean(archivingSurveyId)}
    >
      {shopifyComponentCopy.microSurveysMicroSurveyList.archiveMicroSurvey003}
    </Button>
  ) : null

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center" gap="200">
          <BlockStack gap="100">
            <Text as="h3" variant="headingMd">
              {surveyQuestion(survey)}
            </Text>
            <Text as="p" tone="subdued">
              {triggerLabel(survey)}
            </Text>
          </BlockStack>
          <Badge tone={badge.tone}>{badge.label}</Badge>
        </InlineStack>
        <InlineStack gap="200">
          <Text as="span" tone="subdued">
            {survey.payload.questions[0]?.choices?.length ?? 0}
            {shopifyComponentCopy.microSurveysMicroSurveyList.choices004}
          </Text>
        </InlineStack>
        {archiveAction}
      </BlockStack>
    </Card>
  )
}

function EmptyList() {
  return (
    <Card>
      <BlockStack gap="200">
        <Text as="h2" variant="headingMd">
          {shopifyComponentCopy.microSurveysMicroSurveyList.noMicroSurveysYet005}
        </Text>
        <Text as="p" tone="subdued">
          {shopifyComponentCopy.microSurveysMicroSurveyList.createASupportedStorefrontSurvey006}
        </Text>
      </BlockStack>
    </Card>
  )
}

function ErrorList({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          {shopifyComponentCopy.microSurveysMicroSurveyList.unableToLoadMicroSurveys008}
        </Text>
        <InlineError message={error} fieldID={shopifyComponentCopy.microSurveysMicroSurveyList.microSurveys009} />
        <InlineStack>
          <Button onClick={onRetry}>{shopifyComponentCopy.microSurveysMicroSurveyList.retry010}</Button>
        </InlineStack>
      </BlockStack>
    </Card>
  )
}

/** Shows the loaded micro-surveys without deriving lifecycle state outside the saved record. */
export function MicroSurveyList({
  microSurveys,
  isLoading,
  error,
  archivingSurveyId,
  onRetry,
  onArchive,
}: MicroSurveyListProps) {
  if (isLoading && microSurveys.length === 0)
    return (
      <ShopifyPageSkeleton
        layout="index"
        accessibilityLabel={shopifyComponentCopy.microSurveysMicroSurveyList.loadingMicroSurveys007}
      />
    )

  if (error && microSurveys.length === 0) return <ErrorList error={error} onRetry={onRetry} />

  if (!microSurveys.length) return <EmptyList />

  return (
    <BlockStack gap="300">
      {error && <ErrorList error={error} onRetry={onRetry} />}
      {microSurveys.map((survey) => (
        <MicroSurveyRow
          key={survey.surveyId}
          survey={survey}
          archivingSurveyId={archivingSurveyId}
          onArchive={onArchive}
        />
      ))}
    </BlockStack>
  )
}
